import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { selectProgressLoaded, selectUserProgress } from "../content/contentSlice";

// number of seconds to offset the progress by
// better UX than resuming at the exact point they left off
const progressOffset = 4;
// number of seconds to consider the video near the end
const endingOffset = 60;

// this hook is used to resume video playback where the user left off
export const useVideoProgress = (id, player, duration) => {
  // use playerReady state instead of the player prop because the prop is likely to be a ref
  // state changes trigger re-renders, but refs do not
  // it would cause a race condition to check if player is initialized using a ref
  const [playerReady, setPlayerReady] = useState(false);
  // use ref to avoid re-applying progress after updating state
  const progressAppliedRef = useRef(false);
  const userProgress = useSelector(selectUserProgress(id));
  const progressLoaded = useSelector(selectProgressLoaded);

  useEffect(() => {
    const isNearEnd = duration - userProgress?.progress < endingOffset; // user left off near the end of the video or finished watching
    if (playerReady && progressLoaded && !progressAppliedRef.current && !isNearEnd) {
      if (userProgress && player) player.currentTime(userProgress.progress - progressOffset);
      progressAppliedRef.current = true;
    }
  }, [userProgress, playerReady, player, duration, progressLoaded]);

  const handlePlayerReady = useCallback(() => setPlayerReady(true), []);

  const handleProgressOnSrcChange = useCallback(() => {
    if (!userProgress || !player) return;
    player.currentTime(userProgress.progress - progressOffset);
  }, [userProgress, player]);

  return {
    handlePlayerReady,
    handleProgressOnSrcChange
  };
};
